import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Context/authContext";
import { getRoutesForRole } from "../../Utils/roleRoutes";
import { Typography } from "../Typography";
import Button from "../Button";
import Avatar from "../Avatar";
import { getTasksByUserId } from "../../Utils/apiServices";
import { SidebarProvider } from "../../Context/sidebarContext";

const iconMap = {
  Dashboard: "iconoir:home-simple",
  Assessment: "mdi:chart-line",
  Assignment: "mdi:clipboard-text-outline",
  CalendarToday: "mdi:calendar-blank-outline",
  Checklist: "mdi:checkbox-multiple-marked-outline",
  Image: "mdi:image-outline",
  Description: "mdi:file-document-outline",
  History: "mdi:history",
  Settings: "mdi:cog-outline",
  Database: "mdi:database-cog-outline",
  Help: "mdi:help-circle-outline",
  Article: "mdi:file-document-outline",
  Event: "mdi:calendar-star",
  RateReview: "mdi:comment-quote-outline",
};

const SideNavigation = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [taskCount, setTaskCount] = useState(0);

  // Check if user needs to complete registration
  const needsCompleteRegistration = () => {
    const isProfileCompleted = user?.is_profile_completed;
    
    if (user?.role === "mentor") {
      const mentorStatus = user?.mentor_status;
      return mentorStatus === "pending" || mentorStatus === "submitted" || isProfileCompleted === false;
    }
    
    // For partner and investor, check if profile is incomplete
    if (user?.role === "ecosystem_partner" || user?.role === "corporate_sponsor" || user?.role === "investor") {
      return isProfileCompleted === false;
    }
    
    // For student, check if profile is incomplete
    if (user?.role === "student") {
      return isProfileCompleted === false;
    }
    
    return false;
  };

  // Get menu items based on role and registration status
  const getMenuItems = () => {
    const routes = getRoutesForRole(user?.role || "student");
    
    // If user needs to complete registration, show only Complete Registration tab
    if (needsCompleteRegistration()) {
      return routes
        .filter((route) => route.path === "/complete-registration")
        .map((route) => ({
          text: route.label,
          path: route.path,
          icon: iconMap[route.icon] || "mdi:chevron-right",
        }));
    }
    
    // For mentors, conditionally show Complete Registration based on status
    if (user?.role === "mentor") {
      const mentorStatus = user?.mentor_status;
      const isProfileCompleted = user?.is_profile_completed;
      
      // Show Complete Registration only if status is pending, submitted, or profile is incomplete
      const shouldShowCompleteRegistration = 
        mentorStatus === "pending" || 
        mentorStatus === "submitted" || 
        isProfileCompleted === false;
      
      return routes
        .filter((route) => {
          // Filter out Complete Registration if mentor doesn't need it
          if (route.path === "/complete-registration" && !shouldShowCompleteRegistration) {
            return false;
          }
          return true;
        })
        .map((route) => ({
          text: route.label,
          path: route.path,
          icon: iconMap[route.icon] || "mdi:chevron-right",
        }));
    }
    
    // For partner and investor, conditionally show Complete Registration based on profile completion
    if (user?.role === "ecosystem_partner" || user?.role === "corporate_sponsor" || user?.role === "investor") {
      const isProfileCompleted = user?.is_profile_completed;
      
      // Show Complete Registration only if profile is incomplete
      const shouldShowCompleteRegistration = isProfileCompleted === false;
      
      return routes
        .filter((route) => {
          // Filter out Complete Registration if profile is completed
          if (route.path === "/complete-registration" && !shouldShowCompleteRegistration) {
            return false;
          }
          return true;
        })
        .map((route) => ({
          text: route.label,
          path: route.path,
          icon: iconMap[route.icon] || "mdi:chevron-right",
        }));
    }
    
    // For student, conditionally show Complete Registration based on profile completion
    if (user?.role === "student") {
      const isProfileCompleted = user?.is_profile_completed;
      const shouldShowCompleteRegistration = isProfileCompleted === false;
      
      return routes
        .filter((route) => {
          if (route.path === "/complete-registration" && !shouldShowCompleteRegistration) {
            return false;
          }
          return true;
        })
        .map((route) => ({
          text: route.label,
          path: route.path,
          icon: iconMap[route.icon] || "mdi:chevron-right",
        }));
    }
    
    // Otherwise, show all routes for the role
    return routes.map((route) => ({
      text: route.label,
      path: route.path,
      icon: iconMap[route.icon] || "mdi:chevron-right",
    }));
  };

  const menuItems = getMenuItems();
  
  // Get display name from user
  const getDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "User";
    }
    return user?.email || "User";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  // Fetch task count for the logged-in user
  useEffect(() => {
    const fetchTaskCount = async () => {
      if (!user?.id) {
        setTaskCount(0);
        return;
      }

      try {
        const response = await getTasksByUserId(user.id);
        if (response.success) {
          // Handle different response structures
          let tasksData = [];
          if (Array.isArray(response.data?.data)) {
            tasksData = response.data.data;
          } else if (Array.isArray(response.data)) {
            tasksData = response.data;
          } else if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
            tasksData = response.data.data.data;
          }
          setTaskCount(tasksData.length);
        }
      } catch (error) {
        console.error("Error fetching task count:", error);
        setTaskCount(0);
      }
    };

    fetchTaskCount();
  }, [user?.id]);

  const badgeForItem = (text) => {
    if (text.toLowerCase().includes("task")) {
      return taskCount > 0 ? taskCount : null;
    }
    return null;
  };

  return (
    <SidebarProvider collapsed={collapsed} setCollapsed={setCollapsed}>
      <div className="min-h-screen bg-white text-slate-900 flex">
        {/* Mobile backdrop overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 md:hidden transition-opacity duration-200"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
        className={[
          "fixed inset-y-0 left-0 z-30 w-64 transform bg-white border-r border-slate-200 transition-transform duration-200 ease-in-out flex flex-col",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          !collapsed ? "md:translate-x-0" : "md:-translate-x-full",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex items-center justify-between px-4 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <img src="/ign-logo.svg" alt="IgniteNext Logo" className="h-15 w-50" />
            {/* <div>
              <p className="text-lg font-semibold text-slate-800">IgniteNext</p>
            </div> */}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100"
              onClick={() => setCollapsed(true)}
              aria-label="Close sidebar"
            >
              <Icon icon="mdi:chevron-left" className="h-5 w-5" />
            </button>
            <button
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <Icon icon="mdi:close" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="p-3 space-y-1 flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              className={[
                "w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition",
                isActive(item.path)
                  ? "bg-slate-100 text-slate-900 shadow-inner"
                  : "text-slate-700 hover:bg-slate-50",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Icon icon={item.icon} className="h-5 w-5" />
              <span className="flex-1 text-left">{item.text}</span>
              {badgeForItem(item.text) ? (
                <span className="inline-flex items-center justify-center h-6 min-w-[24px] rounded-full bg-slate-100 text-xs font-semibold text-slate-600 px-2">
                  {badgeForItem(item.text)}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="px-4 pb-4 mt-auto shrink-0">
          {/* Only show Profile button if mentor doesn't need to complete registration */}
          {!needsCompleteRegistration() && (
            <button
              onClick={() => navigate("/profile")}
              className="flex gap-2 items-start justify-start w-full hover:opacity-80 transition-opacity"
            >
              <div className="h-10 w-10 overflow-hidden justify-center items-center">
                <Icon icon="lucide:user" className="h-5 w-5 text-tertiary-600" />
              </div>
              <Typography
                as="p"
                className="text-md font-semibold text-secondary-700"
              >
                Profile
              </Typography>
            </button>
          )}
          <div className="flex items-center gap-3 rounded-2xl border border-borderSecondary bg-primary px-3 py-2">
            <Avatar
              src={user?.profile_picture_url}
              firstName={user?.first_name}
              lastName={user?.last_name}
              email={user?.email}
              alt={getDisplayName()}
              size="w-10 h-10"
              showBorder={true}
              className="border-borderSecondary"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-primary-900 truncate">
                {getDisplayName()}
              </p>
              <p className="text-xs text-tertiary-600 truncate">{user?.email || "user@example.com"}</p>
            </div>
            {/* <div className="h-10 w-10 overflow-hidden justify-center items-center flex-shrink-0">
              <Icon
                icon="lucide:chevron-up"
                className="h-5 w-5 text-tertiary-600"
              />
              <Icon
                icon="lucide:chevron-down"
                className="h-5 w-5 text-tertiary-600"
              />
            </div> */}
          </div>
          <div className="mt-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={handleLogout}
              startIcon={<Icon icon="lucide:log-out" className="h-4 w-4" />}
            >
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col bg-white transition-all duration-200 ${collapsed ? "md:ml-0" : "md:ml-64"}`}>
        {/* Toggle button to open sidebar when collapsed */}
        {collapsed && (
          <button
            className="fixed left-4 top-4 z-20 md:inline-flex hidden h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-100 shadow-sm"
            onClick={() => setCollapsed(false)}
            aria-label="Open sidebar"
          >
            <Icon icon="mdi:menu" className="h-5 w-5" />
          </button>
        )}
        {/* Mobile menu button */}
        {!mobileOpen && (
          <button
            className="fixed left-4 top-4 z-20 md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-100 shadow-sm"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Icon icon="mdi:menu" className="h-5 w-5" />
          </button>
        )}
        <main className={`flex-1 w-full overflow-x-hidden ${
          collapsed 
            ? "px-4 pt-16 md:pl-16 md:pr-4 md:pt-16" 
            : "px-4 pt-16 pb-6 md:px-6 md:pt-6 lg:px-8"
        }`}>
          <div className="w-full max-w-full">{children}</div>
        </main>
      </div>
    </div>
    </SidebarProvider>
  );
};

export default SideNavigation;
